import * as React from 'react';
import { Dispatch, connect } from 'react-redux';
import { IApplicationState, AppActionCreators, IAppState, MicrositeActionCreators, IMicrositeCampaignResponse } from '@nkr/store/index';
import { IBaseResponse } from '@nkr/models/index';
import { Alert, Badge, Button, Col, ControlLabel, FormGroup, InputGroup, Panel, Row, Well } from 'react-bootstrap';
import Dropzone, { DropEvent, FileRejection } from 'react-dropzone';
import CampaignSteps from './campaign-steps';
import { FormWrapper } from '../styles';
import { Container } from './styles';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';

interface IProps {
    campaignId: number,
    centerId: number,
    isAdmin: boolean,
    previousUrl: string
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getCampaign: (id: number) => Promise<IMicrositeCampaignResponse>,
    updateCampaignMailingList: (id: number, file: File) => Promise<IBaseResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    id: number,
    campaignName: string,
    startDate: string,
    endDate: string,
    processedByNkr: boolean,
    existingFileName: string,
    files: File[]
}

class CampaignMailingList extends React.Component<TProps, IState> {
    readonly state: IState = {
        id: 0,
        campaignName: '',
        startDate: '',
        endDate: '',
        processedByNkr: true,
        existingFileName: '',
        files: []
    }

    componentDidMount = () => {
        this.props.showLoading('Getting the campaign, one moment please...').then(() => {
            this.props.getCampaign(this.props.campaignId).then(response => {
                if (response.data.success) {
                    let { id, campaignName, startDate, endDate, mailingListFileName: existingFileName, processedByNkr } = response.data.results;

                    this.setState({
                        id,
                        campaignName,
                        startDate,
                        endDate,
                        processedByNkr,
                        existingFileName
                    });
                }

                this.props.hideLoading();
            });
        });
    }

    handleFileDropped = (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
        this.setState({
            files: acceptedFiles
        });
    }

    handleRemoveFile = () => {
        this.setState({
            files: []
        });
    }

    handleSubmit = () => {
        this.props.showLoading('Uploading file, one moment please...').then(() => {
            this.props.updateCampaignMailingList(this.state.id, this.state.files[0])
                .then(response => {
                    this.props.hideLoading();

                    if (response.data.success) {
                        toast.success('Mailing list successfully uploaded, going to the mapping section...', {
                            onClose: () => {
                                window.location.href = this.getNextUrl();
                            }
                        });
                    }
                })
                .catch((error: AxiosError) => {
                    this.props.hideLoading();
                    if(error.response && error.response.data && error.response.data.includes("No such host"))
                        toast.error('Unable to upload the mailing list, the following error occurred: ' + " No host running on the server.");
                    else
                        toast.error('Unable to upload the mailing list, the following error occurred: ' + error.message);
                });
        });
    }

    handleCancel = () => {
        let href = this.getReturnUrl();
        window.location.href = href;
    }

    getNextUrl = () => {
        let href = this.props.isAdmin ? `/admin/microsite/campaign/${this.state.id}/mailing-list-mapping` : `/center/${this.props.centerId}/microsite/campaign/${this.state.id}/mailing-list-mapping`;
        return href;
    }

    getReturnUrl = () => {
        let href = this.props.isAdmin ? `/admin/microsite/campaigns` : `/center/${this.props.centerId}/microsite/campaigns`;
        return href;
    }

    render = () => {
        return (
            <>
                <Row>
                    <Col xs={12} md={8} mdOffset={2}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, marginBottom: 25 }}>
                            <h1 className='text-left' style={{ margin: 0 }}>Upload Campaign Mailing List</h1>
                            <div className='pull-right'>
                                <Button bsStyle='link' onClick={this.handleCancel}>Return to Campaign List</Button>
                            </div>
                        </div>

                        <Alert bsStyle='info'>
                            <div style={{ marginBottom: 10 }}>We only accept <strong>CSV</strong> formatted files (<a href='/lib/templates/mailing-list-sample.csv'>Click here</a> for a sample CSV file...)</div>
                            <div style={{ marginBottom: 10 }}>Please ensure that the file contains a column for each of the following: FIRST NAME, LAST NAME, ADDRESS LINE 1, ADDRESS LINE 2, CITY, STATE, POSTAL CODE</div>
                            <div>It is not mandatory that the first row contains column headers</div>
                        </Alert>

                        <Well>
                            <Row>
                                <Col xs={12} sm={4} componentClass={ControlLabel}>Campaign:</Col>
                                <Col xs={12} lg={8}>{this.state.campaignName}</Col>
                            </Row>

                            <Row>
                                <Col xs={12} sm={4} componentClass={ControlLabel}>Campaign Type:</Col>
                                <Col xs={12} lg={8}>Mailed Out</Col>
                            </Row>

                            <Row>
                                <Col xs={12} sm={4} componentClass={ControlLabel}>NKR will process the mailing:</Col>
                                <Col xs={12} lg={8}>{this.state.processedByNkr ? 'Yes' : 'No'}</Col>
                            </Row>
                        </Well>

                        <FormGroup>
                            <Dropzone
                                onDrop={this.handleFileDropped}
                                noKeyboard
                                accept={'.csv'}
                            >
                                {({ getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject }) => {
                                    return (
                                        <Container {...getRootProps({ isDragActive, isDragAccept, isDragReject })}>
                                            <div style={{ width: '100%', height: 50, display: 'flex', alignItems: 'center' }}>
                                                <div style={{ margin: 'auto', textAlign: 'center' }}>
                                                    <div>Click here to select the campaign mailing list file or drag &amp; drop the campaign mailing list file...</div>

                                                    {this.state.existingFileName != '' && (
                                                        <Badge>This will replace the existing file</Badge>
                                                    )}
                                                </div>
                                            </div>
                                            <input {...getInputProps()} />
                                        </Container>
                                    );
                                }}
                            </Dropzone>
                        </FormGroup>

                        <FormGroup>
                            {this.state.files.length > 0 && (
                                <InputGroup style={{ marginBottom: 20 }}>
                                    <InputGroup.Addon>File to upload:</InputGroup.Addon>
                                    <InputGroup.Addon style={{ width: 'initial', backgroundColor: 'white' }}>
                                        {this.state.files.map((file, index) =>
                                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                <div style={{ marginRight: 10, color: 'black' }}>{file.name}</div>
                                                <div style={{ display: 'flex' }}>
                                                    <Button bsStyle='danger' bsSize='xs' onClick={this.handleRemoveFile}>Remove</Button>
                                                </div>
                                            </div>
                                        )}
                                    </InputGroup.Addon>
                                </InputGroup>
                            )}

                            {this.state.existingFileName != '' && (
                                <div className='text-center'>File previously uploaded: {this.state.existingFileName}</div>
                            )}
                        </FormGroup>


                        <Well bsSize='sm'>
                            <CampaignSteps currentStep={1} campaignId={this.props.campaignId} />
                        </Well>

                        <FormGroup>
                            <div className='col-xs-12 col-lg-12 text-center'>
                                <Button bsStyle='primary' disabled={this.state.files.length == 0} onClick={this.handleSubmit}>Upload &amp; Go to the Column Mapping</Button>
                                <Button bsStyle='link' onClick={this.handleCancel}>Cancel &amp; Return to the Campaign List</Button>
                            </div>
                        </FormGroup>
                    </Col>
                </Row>
            </>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => { return dispatch(AppActionCreators.showLoadingAsync(message)); },
        hideLoading: () => { return dispatch(AppActionCreators.hideLoading()); },
        getCampaign: (id) => { return dispatch(MicrositeActionCreators.getCampaign(id)); },
        updateCampaignMailingList: (id, file) => { return dispatch(MicrositeActionCreators.uploadCampaignMailingList(id, file)); }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(CampaignMailingList);